// Quarks
import { type FC, useEffect, useRef, useState } from 'react';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';
import { HeadingTypes, TextStyles } from 'quarks/styleProps/heading';

import ComponentButton from 'components/Button/ComponentButton';
import SubDomainForm from 'components/Form/SubDomainForm';
import { ExpandContent, GatedForm } from 'components/Heading/ExpandableComponents';
import Body from 'components/ResourceDetailLayout/components/Body';
import Sidebar from 'components/ResourceDetailLayout/components/Sidebar/Sidebar';

import { formatEyebrowSize, formatHeadingSize } from 'utils/formatHeadingSize';
import { colorTheme, toCamelCase } from 'utils/functions';
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentHeading } from 'graphqlTypes';

interface HeadingProps extends FlexProps, ContentfulComponentHeading {
  backgroundIsDark: boolean;
  isLeft?: boolean;
}

const ComponentHeading: FC<HeadingProps> = ({
  eyebrow,
  eyebrowType,
  heading,
  headingType,
  headingColor,
  body,
  bodyColor,
  callToAction,
  backgroundIsDark,
  isLeft,
  isFullWidth,
  headingAlignment,
  icon,
  loginField,
  tableOfContents,
  expandable,
  gatedContent,
  ...props
}) => {
  const [isExpandable, toggleExpandable] = useState(expandable || gatedContent);
  const ref = useRef();

  const { headingSizeMobile, headingSizeTablet, headingSizeDesktop } = formatHeadingSize(props);
  const { eyebrowSizeMobile, eyebrowSizeTablet, eyebrowSizeDesktop } = formatEyebrowSize(props);

  const leftAlign = isLeft || headingAlignment;
  const hasToc = tableOfContents?.filter(x => x?.content?.[0]?.isVisible).length > 0;

  useEffect(() => {
    if (ref?.current) {
      ref.current.parentNode.style.maxHeight = isExpandable ? '570px' : 'none';
      ref.current.parentNode.style.overflow = isExpandable ? 'hidden' : 'unset';
    }
  }, [isExpandable]);

  return (
    <Flex
      ref={ref}
      position="relative"
      flexDirection="column"
      textAlign={leftAlign ? 'left' : 'center'}
      alignItems={leftAlign ? 'flex-start' : 'center'}
      gap="24px"
      width="100%"
      maxWidth={isFullWidth ? '100%' : '770px'}
      maxHeight="inherit"
      marginX={leftAlign ? undefined : 'auto'}
      textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
      {...props}
    >
      {expandable && !gatedContent && isExpandable && <ExpandContent onClick={() => toggleExpandable(false)} />}
      {gatedContent && isExpandable && <GatedForm onSuccess={() => toggleExpandable(false)} />}
      {eyebrow && (
        <Text
          data-testid="richtext-eyebrow"
          as={(eyebrowType?.toLowerCase() as HeadingTypes) || 'span'}
          textTransform="uppercase"
          fontWeight="semiBold"
          textColor={backgroundIsDark ? 'common-white' : 'blue-600'}
          fontSize={eyebrowSizeMobile || 'textMd'}
          md={{ fontSize: eyebrowSizeTablet || 'textMd', lineHeight: 'textMd' }}
          lg={{ fontSize: eyebrowSizeDesktop || 'textMd', lineHeight: 'textMd' }}
        >
          {eyebrow}
        </Text>
      )}
      {heading && (
        <Heading
          data-testid="richtext-heading"
          as={(headingType?.toLowerCase() as HeadingTypes) || 'h2'}
          textStyle={(headingSizeMobile as TextStyles) || 'h3'}
          fontWeight="bold"
          textColor={
            backgroundIsDark && !headingColor ? 'common-white' : colorTheme(toCamelCase(headingColor || 'Gray 900'))
          }
          md={{ textStyle: (headingSizeTablet as TextStyles) || 'h3', fontWeight: 'bold' }}
          lg={{ textStyle: (headingSizeDesktop as TextStyles) || 'h2', fontWeight: 'bold' }}
        >
          {heading}
        </Heading>
      )}
      {body && (
        <Flex
          data-testid="richtext-body"
          flexDirection={hasToc ? 'column-reverse' : 'column'}
          gap={hasToc ? '32px' : '16px'}
          textAlign={hasToc ? 'left' : leftAlign ? 'left' : 'center'}
          lg={{ flexDirection: hasToc ? 'row' : 'column' }}
          width="100%"
          textColor={backgroundIsDark && !bodyColor ? 'gray-200' : colorTheme(bodyColor || 'Gray 700')}
          css={`
            ul,
            ol {
              text-align: left;
              width: 100%;
            }
          `}
        >
          {hasToc ? (
            <Flex
              data-testid="richtext-toc"
              width="100%"
              justifyContent="space-between"
              flexDirection="column-reverse"
              lg={{ flexDirection: 'row' }}
            >
              <Body body={body} />
              <Sidebar tocProps={{ tableOfContents }} />
            </Flex>
          ) : (
            richTextParser(body)
          )}
        </Flex>
      )}
      {callToAction && (
        <Flex
          data-testid="richtext-cta"
          flexDirection="column"
          width="100%"
          gap="16px"
          justifyContent={leftAlign ? 'flex-start' : 'center'}
          alignItems={leftAlign ? 'flex-start' : 'center'}
          sm={{ flexDirection: 'row' }}
        >
          {callToAction.map(button => (
            <ComponentButton key={button?.contentful_id} {...button} />
          ))}
        </Flex>
      )}
      {icon && icon.length > 0 && (
        <Flex
          data-testid="richtext-icon"
          alignItems="center"
          justifyContent="center"
          width="100%"
          gap="32px"
          marginTop={8}
        >
          {icon.map(item => (
            <Link key={item?.iconLink} href={item?.iconLink || ''}>
              <Image
                image={item?.icon as ImageData}
                alt={item?.icon?.title || 'Social Icon'}
                width="24px"
                height="24px"
                white={backgroundIsDark}
              />
            </Link>
          ))}
        </Flex>
      )}
      {loginField && (
        <Flex data-testid="richtext-login" width="100%" maxWidth="600px" marginX="auto">
          <SubDomainForm />
        </Flex>
      )}
    </Flex>
  );
};

export default ComponentHeading;
